import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Input, GenderToggle, Textarea, LoadingSpinner } from '@app/shared/ui/atoms/Common';
import { DateOfBirthInput } from '@app/shared/ui/molecules/Common';
import {
  ProfileFieldWithLabel,
  ProfileSelectVirtualized,
  PowerSwitch,
} from '@app/shared/ui/molecules/EditProfile';
import { useUserProfileForm } from '@app/shared/hooks/useUserProfileForm';
import { useDynamicTranslationNamespace } from '@app/shared/hooks/useDynamicTranslationNamespace';
import { getShortLabel, mapToOptions, getSafeOptions } from '@app/shared/utils/userProfile';
import { telegramVibrate } from '@app/shared/utils/common';
import { CountriesData, CitiesData, ActivitiesData } from '@app/shared/types/pages/userProfile';

export function ProfileForm() {
  const { t, i18n } = useTranslation();

  const {
    isUpdatingCurrentUserProfile,
    dateOfBirth,
    dateOfBirthErrorTranslationKey,
    handleDateOfBirthChange,
    handleDateOfBirthClear,
    formState,
    handleFormStateChange,
    handleFormSubmit,
    hasFormError,
  } = useUserProfileForm();
  const loadedCountriesTranslations = useDynamicTranslationNamespace('countries');

  const countryOptions = useMemo(() => {
    if (!loadedCountriesTranslations) {
      return [];
    }
    return getSafeOptions<CountriesData>(mapToOptions, t, 'countries', 'countries');
  }, [i18n.language, loadedCountriesTranslations]);

  const cityOptions = useMemo(
    () =>
      getSafeOptions<CitiesData>(
        (cities) => mapToOptions(cities[formState.country ?? 'bs'] ?? {}),
        t,
        'cities',
        'countries'
      ),
    [i18n.language, loadedCountriesTranslations, formState.country]
  );
  const activityOptions = useMemo(
    () => getSafeOptions<ActivitiesData>(mapToOptions, t, 'activities'),
    [i18n.language]
  );

  if (!loadedCountriesTranslations) {
    return <LoadingSpinner />;
  }

  return (
    <form className="mt-6 flex flex-col gap-2">
      <ProfileFieldWithLabel label={t('edit_profile.full_name')}>
        <Input
          value={formState.fullName ?? ''}
          onChange={(e) => {
            handleFormStateChange('fullName', e.target.value);
          }}
          placeholder={`${t('edit_profile.full_name')}...`}
        />
      </ProfileFieldWithLabel>

      <ProfileFieldWithLabel
        label={t('edit_profile.birthday')}
        error={dateOfBirthErrorTranslationKey && t(dateOfBirthErrorTranslationKey)}
        hasErrorField
      >
        <DateOfBirthInput
          day={dateOfBirth.day}
          month={dateOfBirth.month}
          year={dateOfBirth.year}
          align="center"
          onChange={handleDateOfBirthChange}
          onClear={handleDateOfBirthClear}
        />
      </ProfileFieldWithLabel>

      <ProfileFieldWithLabel label={t('edit_profile.gender')}>
        <GenderToggle
          value={formState.gender ?? 'm'}
          onChange={(value) => {
            telegramVibrate();
            handleFormStateChange('gender', value);
          }}
        />
      </ProfileFieldWithLabel>

      <ProfileFieldWithLabel label={t('edit_profile.instagram')}>
        <Input
          value={formState.instagramTag ?? undefined}
          onChange={(e) => {
            handleFormStateChange('instagramTag', e.target.value);
          }}
          placeholder={`${getShortLabel(t('edit_profile.instagram'))}...`}
        />
      </ProfileFieldWithLabel>

      <ProfileFieldWithLabel label={t('edit_profile.telegram')}>
        <Input
          value={formState.telegramUsername ?? undefined}
          placeholder={`${getShortLabel(t('edit_profile.telegram'))}...`}
          disabled
        />
      </ProfileFieldWithLabel>

      <ProfileFieldWithLabel label={t('common.bio')}>
        <Textarea
          value={formState.bio ?? undefined}
          onChange={(e) => {
            handleFormStateChange('bio', e.target.value);
          }}
          variant="dark-blue"
          placeholder={`${t('common.bio')}...`}
        />
      </ProfileFieldWithLabel>

      {countryOptions.length > 0 && (
        <>
          <ProfileFieldWithLabel label={t('edit_profile.country')}>
            <ProfileSelectVirtualized
              value={formState.country ?? ''}
              onChange={(value) => {
                handleFormStateChange('country', value);
                handleFormStateChange('city', '');
              }}
              options={countryOptions}
              placeholder={t('common.select')}
            />
          </ProfileFieldWithLabel>
          <ProfileFieldWithLabel label={t('edit_profile.city')}>
            <ProfileSelectVirtualized
              value={formState.city ?? ''}
              onChange={(value) => {
                if (value) {
                  handleFormStateChange('city', value);
                }
              }}
              options={cityOptions}
              placeholder={t('common.select')}
              isDisabled={!formState.country}
            />
          </ProfileFieldWithLabel>
        </>
      )}

      <ProfileFieldWithLabel label={t('edit_profile.activity')}>
        <ProfileSelectVirtualized
          value={formState.activitiesScope ?? ''}
          onChange={(value) => {
            if (value) {
              handleFormStateChange('activitiesScope', value);
            }
          }}
          options={activityOptions}
          placeholder={t('common.select')}
        />
      </ProfileFieldWithLabel>

      <ProfileFieldWithLabel label={t('edit_profile.settlements_auto_purchase')}>
        <PowerSwitch
          checked={formState.autoPurchase}
          onCheckedChange={(value) => {
            telegramVibrate();
            handleFormStateChange('autoPurchase', value);
          }}
        />
      </ProfileFieldWithLabel>

      <ProfileFieldWithLabel label={t('edit_profile.feed_panda_ntf')}>
        <PowerSwitch
          checked={formState.feedPandaNtf}
          onCheckedChange={(value) => {
            telegramVibrate();
            handleFormStateChange('feedPandaNtf', value);
          }}
        />
      </ProfileFieldWithLabel>

      {hasFormError && (
        <div className="px-3 mt-2 text-center text-sm text-red-500">{t('errors.form_general')}</div>
      )}

      <div className="px-3">
        <Button
          type="button"
          shadow="blue"
          onClick={handleFormSubmit}
          disabled={isUpdatingCurrentUserProfile}
        >
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}
